import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import UploadField from "./UploadField";
import { api, formatApiError } from "../../lib/api";

const inputCls = "w-full bg-[#0D1117] border border-[#263342] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-crimson transition-colors";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2";
const sectionCls = "bg-[#0D1117] border border-[#263342] p-6";

function Section({ title, hint, children }) {
  return (
    <div className={sectionCls}>
      <h3 className="font-display font-bold uppercase tracking-tight text-xl text-slate-100">{title}</h3>
      {hint && <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">{hint}</p>}
      <div className="mt-5 space-y-4">{children}</div>
    </div>
  );
}

const SECTION_HEADINGS = [
  { key: "services", label: "Services section" },
  { key: "inventory", label: "Vehicles for sale section" },
  { key: "about", label: "About section" },
  { key: "gallery", label: "Gallery section" },
  { key: "testimonials", label: "Testimonials section" },
  { key: "videos", label: "YouTube videos section" },
];

export default function SiteContentManager() {
  const [content, setContent] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/content");
      setContent(data);
    } catch (err) {
      toast.error("Could not load site content", { description: formatApiError(err) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setField = (key, value) => setContent((c) => ({ ...c, [key]: value }));
  const setListItem = (key, i, patch) =>
    setContent((c) => ({ ...c, [key]: c[key].map((item, idx) => (idx === i ? { ...item, ...patch } : item)) }));
  const addListItem = (key, blank) => setContent((c) => ({ ...c, [key]: [...(c[key] || []), blank] }));
  const removeListItem = (key, i) => setContent((c) => ({ ...c, [key]: c[key].filter((_, idx) => idx !== i) }));
  const setLine = (i, v) =>
    setContent((c) => ({ ...c, hero_lines: c.hero_lines.map((l, idx) => (idx === i ? v : l)) }));

  const save = async () => {
    setSaving(true);
    try {
      const { id, ...payload } = content;
      await api.put("/content", payload);
      toast.success("Site content saved", { description: "Changes are live on the website now." });
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err) });
    } finally {
      setSaving(false);
    }
  };

  if (!content) {
    return <p className="font-mono text-sm text-slate-500 py-10 text-center" data-testid="content-loading">Loading site content...</p>;
  }

  return (
    <div className="space-y-6" data-testid="site-content-manager">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold uppercase tracking-tight text-2xl text-slate-100">Site Content</h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mt-1">Edit text, photos and details — goes live instantly</p>
        </div>
        <button
          data-testid="content-save-button"
          onClick={save}
          disabled={saving}
          className="bg-crimson text-white font-display font-semibold uppercase tracking-widest text-sm px-6 py-3 hover:-translate-y-0.5 transition-transform disabled:opacity-60"
        >
          {saving ? "Saving..." : "Save All Changes"}
        </button>
      </div>

      <Section title="Hero" hint="The big headline at the top of the page">
        <div>
          <label className={labelCls}>Eyebrow label</label>
          <input className={inputCls} data-testid="content-est-label" value={content.est_label} onChange={(e) => setField("est_label", e.target.value)} />
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {(content.hero_lines || []).map((line, i) => (
            <div key={i}>
              <label className={labelCls}>Headline line {i + 1}</label>
              <input className={inputCls} data-testid={`content-hero-line-${i}`} value={line} onChange={(e) => setLine(i, e.target.value)} />
            </div>
          ))}
        </div>
        <div>
          <label className={labelCls}>Subtext</label>
          <textarea rows={2} className={inputCls} data-testid="content-hero-subtext" value={content.hero_subtext} onChange={(e) => setField("hero_subtext", e.target.value)} />
        </div>
        <UploadField
          label="Background photo"
          testid="content-hero-image"
          value={content.hero_image}
          onChange={(url) => setField("hero_image", url)}
        />
      </Section>

      <Section title="Section Headings" hint="The small red label and big title above each section">
        {SECTION_HEADINGS.map((s) => (
          <div key={s.key} className="grid sm:grid-cols-2 gap-4" data-testid={`content-heading-${s.key}`}>
            <div>
              <label className={labelCls}>{s.label} — small label</label>
              <input className={inputCls} data-testid={`content-${s.key}-eyebrow`} value={content[`${s.key}_eyebrow`] || ""} onChange={(e) => setField(`${s.key}_eyebrow`, e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>{s.label} — big title</label>
              <input className={inputCls} data-testid={`content-${s.key}-title`} value={content[`${s.key}_title`] || ""} onChange={(e) => setField(`${s.key}_title`, e.target.value)} disabled={s.key === "about"} placeholder={s.key === "about" ? "Edited in About Section below" : ""} />
            </div>
          </div>
        ))}
      </Section>

      <Section title="Scrolling Ticker" hint="The slow-moving text strip under the hero — one phrase per line">
        <textarea
          rows={5}
          className={inputCls}
          data-testid="content-marquee-items"
          value={(content.marquee_items || []).join("\n")}
          onChange={(e) => setField("marquee_items", e.target.value.split("\n").map((l) => l.trim()).filter(Boolean))}
        />
      </Section>

      <Section title="About Section" hint="The workshop story and checkmarks">
        <div>
          <label className={labelCls}>Title</label>
          <input className={inputCls} data-testid="content-about-title" value={content.about_title} onChange={(e) => setField("about_title", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Text</label>
          <textarea rows={3} className={inputCls} data-testid="content-about-text" value={content.about_text} onChange={(e) => setField("about_text", e.target.value)} />
        </div>
        <UploadField
          label="Photo"
          testid="content-about-image"
          value={content.about_image}
          onChange={(url) => setField("about_image", url)}
        />
        <div>
          <label className={labelCls}>Checkmark points (one per line)</label>
          <textarea
            rows={4}
            className={inputCls}
            data-testid="content-about-points"
            value={(content.about_points || []).join("\n")}
            onChange={(e) => setField("about_points", e.target.value.split("\n").filter((l) => l.trim()))}
          />
        </div>
      </Section>

      <Section title="Stats" hint="The number cards under the about section">
        {(content.stats || []).map((s, i) => (
          <div key={i} className="flex gap-3 items-end" data-testid={`content-stat-row-${i}`}>
            <div className="w-28">
              <label className={labelCls}>Value</label>
              <input className={inputCls} data-testid={`content-stat-value-${i}`} value={s.value} onChange={(e) => setListItem("stats", i, { value: e.target.value })} />
            </div>
            <div className="flex-1">
              <label className={labelCls}>Label</label>
              <input className={inputCls} data-testid={`content-stat-label-${i}`} value={s.label} onChange={(e) => setListItem("stats", i, { label: e.target.value })} />
            </div>
            <button onClick={() => removeListItem("stats", i)} data-testid={`content-stat-remove-${i}`} className="p-3 border border-[#263342] text-slate-400 hover:border-red-500 hover:text-red-400 transition-colors" aria-label="Remove stat">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button onClick={() => addListItem("stats", { value: "", label: "" })} data-testid="content-stat-add" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] border border-[#263342] px-4 py-2.5 text-slate-300 hover:border-crimson hover:text-crimson transition-colors">
          <Plus size={14} /> Add Stat
        </button>
      </Section>

      <Section title="Gallery" hint="Photos in the Inside the Haus section">
        {(content.gallery || []).map((g, i) => (
          <div key={i} className="flex gap-3 items-start" data-testid={`content-gallery-row-${i}`}>
            <div className="flex-1 space-y-2">
              <UploadField
                testid={`content-gallery-src-${i}`}
                value={g.src}
                onChange={(url) => setListItem("gallery", i, { src: url })}
              />
              <input className={inputCls} data-testid={`content-gallery-caption-${i}`} value={g.caption} onChange={(e) => setListItem("gallery", i, { caption: e.target.value })} placeholder="Caption" />
            </div>
            <button onClick={() => removeListItem("gallery", i)} data-testid={`content-gallery-remove-${i}`} className="p-3 border border-[#263342] text-slate-400 hover:border-red-500 hover:text-red-400 transition-colors" aria-label="Remove photo">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button onClick={() => addListItem("gallery", { src: "", caption: "" })} data-testid="content-gallery-add" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] border border-[#263342] px-4 py-2.5 text-slate-300 hover:border-crimson hover:text-crimson transition-colors">
          <Plus size={14} /> Add Photo
        </button>
      </Section>

      <Section title="YouTube Videos" hint="Paste links from your channel — the section appears on the site once you add at least one">
        {(content.youtube_videos || []).map((v, i) => (
          <div key={i} className="flex gap-3 items-start" data-testid={`content-video-row-${i}`}>
            <div className="flex-1 space-y-2">
              <input className={inputCls} data-testid={`content-video-url-${i}`} value={v.url} onChange={(e) => setListItem("youtube_videos", i, { url: e.target.value })} placeholder="https://www.youtube.com/watch?v=..." />
              <input className={inputCls} data-testid={`content-video-title-${i}`} value={v.title || ""} onChange={(e) => setListItem("youtube_videos", i, { title: e.target.value })} placeholder="Video title (optional)" />
            </div>
            <button onClick={() => removeListItem("youtube_videos", i)} data-testid={`content-video-remove-${i}`} className="p-3 border border-[#263342] text-slate-400 hover:border-red-500 hover:text-red-400 transition-colors" aria-label="Remove video">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button onClick={() => addListItem("youtube_videos", { url: "", title: "" })} data-testid="content-video-add" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] border border-[#263342] px-4 py-2.5 text-slate-300 hover:border-crimson hover:text-crimson transition-colors">
          <Plus size={14} /> Add Video
        </button>
      </Section>

      <Section title="Booking Time Slots" hint="Times customers can pick when booking (one per line, 24h format)">
        <textarea
          rows={5}
          className={inputCls}
          data-testid="content-time-slots"
          value={(content.time_slots || []).join("\n")}
          onChange={(e) => setField("time_slots", e.target.value.split("\n").map((l) => l.trim()).filter(Boolean))}
        />
      </Section>

      <Section title="Footer" hint="Contact details and wording at the bottom of every page">
        <div>
          <label className={labelCls}>Short business description</label>
          <textarea rows={2} className={inputCls} data-testid="content-footer-blurb" value={content.footer_blurb || ""} onChange={(e) => setField("footer_blurb", e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Address (one line per row)</label>
          <textarea rows={2} className={inputCls} data-testid="content-footer-address" value={content.footer_address} onChange={(e) => setField("footer_address", e.target.value)} />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>Email</label>
            <input className={inputCls} data-testid="content-footer-email" value={content.footer_email} onChange={(e) => setField("footer_email", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Phone number</label>
            <input className={inputCls} data-testid="content-footer-phone" value={content.footer_phone || ""} onChange={(e) => setField("footer_phone", e.target.value)} placeholder="+1 (555) 123-4567" />
          </div>
          <div>
            <label className={labelCls}>Hours</label>
            <input className={inputCls} data-testid="content-footer-hours" value={content.footer_hours} onChange={(e) => setField("footer_hours", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Coordinates line</label>
            <input className={inputCls} data-testid="content-footer-coords" value={content.footer_coords || ""} onChange={(e) => setField("footer_coords", e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Bottom tagline</label>
            <input className={inputCls} data-testid="content-footer-tagline" value={content.footer_tagline || ""} onChange={(e) => setField("footer_tagline", e.target.value)} />
          </div>
        </div>
      </Section>

      <button
        data-testid="content-save-button-bottom"
        onClick={save}
        disabled={saving}
        className="w-full bg-crimson text-white font-display font-semibold uppercase tracking-widest py-4 hover:-translate-y-0.5 transition-transform disabled:opacity-60"
      >
        {saving ? "Saving..." : "Save All Changes"}
      </button>
    </div>
  );
}
